import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { Subscription, filter, firstValueFrom } from 'rxjs';
import { AuthSessionService } from '../../core/auth-session.service';
import { SignalRNotificationService } from '../../core/signalr-notification.service';
import { CsrWebApiService } from '../../csr-web-api.service';
import { NotificationResponse } from '../../models';
import {
  isNotificationActionRequired,
  notificationDeepLinkRoute,
} from '../../features/learner/notifications/notification-routing.util';
import { environment } from '../../../environments/environment';
import { resolveOnboardingRouteFromJourney } from '../../core/onboarding-route-resolver';

@Component({
  selector: 'app-site-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './site-header.component.html',
  styleUrl: './site-header.component.scss',
})
export class SiteHeaderComponent implements OnInit, OnChanges, OnDestroy {
  private readonly router = inject(Router);
  private readonly api = inject(CsrWebApiService);
  readonly auth = inject(AuthSessionService);
  private readonly signalR = inject(SignalRNotificationService);

  @Input() variant: 'marketing' | 'learner' | 'portal' = 'marketing';

  minimal = false;
  readonly mobileOpen = signal(false);
  readonly accountMenuOpen = signal(false);
  readonly notificationsMenuOpen = signal(false);
  private currentPath = '/';
  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private routerSub: Subscription | null = null;
  private signalRSub = new Subscription();

  unseenCount = 0;
  actionRequiredCount = 0;
  recentNotifications: NotificationResponse[] = [];
  notificationsLoading = false;
  /** Soft nudge for learners who have not verified email yet. */
  emailVerificationNeeded = false;

  /** Partner portal capability flags (loaded when variant is portal). */
  portalReferralEnabled = false;
  portalPartnerEnabled = false;
  portalAccountName = '';
  private portalCapsLoaded = false;

  readonly marketingSiteUrl = environment.marketingSiteUrl;
  readonly ctaUrl = 'https://forms.gle/Fm6uNtatZcvExBzCA';

  constructor() {
    this.updateFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.updateFromUrl((e as NavigationEnd).urlAfterRedirects);
        this.closeMobile();
        this.closeAccountMenu();
        this.closeNotificationsMenu();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['variant'] && this.variant === 'portal') {
      void this.loadPortalCapabilities();
    }
  }

  ngOnInit(): void {
    if (this.isAuthenticatedLearner) {
      void this.refreshUnseenCount();
      void this.refreshEmailVerificationBanner();
      this.pollHandle = setInterval(() => {
        if (this.isAuthenticatedLearner) {
          void this.refreshUnseenCount();
        }
      }, 60000);
    }

    if (this.variant === 'portal') {
      void this.loadPortalCapabilities();
    }

    this.signalRSub.add(
      this.signalR.notification$.subscribe((notification) => {
        this.unseenCount++;
        if (isNotificationActionRequired(notification)) {
          this.actionRequiredCount++;
        }
        if (this.recentNotifications.length > 0) {
          this.recentNotifications = [
            notification,
            ...this.recentNotifications.slice(0, 7),
          ];
        }
      }),
    );

    this.signalRSub.add(
      this.signalR.allNotificationsSeen$.subscribe(() => {
        this.unseenCount = 0;
      }),
    );

    this.signalRSub.add(
      this.signalR.notificationRead$.subscribe((id) => {
        const found = this.recentNotifications.find((n) => n.id === id);
        if (found) {
          found.isRead = true;
        }
      }),
    );

    this.signalRSub.add(
      this.signalR.notificationSeen$.subscribe((id) => {
        const found = this.recentNotifications.find((n) => n.id === id);
        if (found) {
          found.isSeen = true;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.signalRSub.unsubscribe();
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
    }
  }

  get isAuthenticatedLearner(): boolean {
    return this.variant === 'learner' && this.auth.isLearner();
  }

  get showEmailVerificationBanner(): boolean {
    return (
      this.isAuthenticatedLearner &&
      this.emailVerificationNeeded &&
      !this.currentPath.startsWith('/account')
    );
  }

  get isAuthenticatedPortal(): boolean {
    return (
      this.variant === 'portal' &&
      (this.auth.isAccount() || this.auth.isAdmin())
    );
  }

  get showSignInCta(): boolean {
    return (
      this.variant === 'learner' &&
      !this.minimal &&
      !this.auth.isLearner() &&
      !this.currentPath.startsWith('/learner/login')
    );
  }

  get hasRightActions(): boolean {
    return (
      this.isAuthenticatedLearner ||
      this.isAuthenticatedPortal ||
      this.showSignInCta ||
      (this.variant === 'marketing' && !this.minimal)
    );
  }

  get showPublicLinks(): boolean {
    if (this.minimal || this.variant === 'portal') {
      return false;
    }
    return this.variant !== 'learner' || !this.auth.isLearner();
  }

  get logoHref(): string {
    if (this.variant === 'portal') {
      return '/portal';
    }
    return this.variant === 'learner' ? this.marketingSiteUrl : '/';
  }

  get accountActive(): boolean {
    return (
      this.currentPath.startsWith('/account') ||
      this.currentPath.startsWith('/achievements') ||
      this.currentPath.startsWith('/job-readiness')
    );
  }

  get notificationsActive(): boolean {
    return this.currentPath.startsWith('/notifications');
  }

  get badgeLabel(): string {
    if (this.unseenCount <= 0) {
      return '';
    }
    return this.unseenCount > 9 ? '9+' : String(this.unseenCount);
  }

  formatTimeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) return 'just now';
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24)
      return `${diffHour} ${diffHour === 1 ? 'hour' : 'hours'} ago`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 30) return `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`;
    const diffMonth = Math.floor(diffDay / 30);
    if (diffMonth < 12)
      return `${diffMonth} ${diffMonth === 1 ? 'month' : 'months'} ago`;
    const diffYear = Math.floor(diffMonth / 12);
    return `${diffYear} ${diffYear === 1 ? 'year' : 'years'} ago`;
  }

  isRouteActive(prefix: string): boolean {
    return (
      this.currentPath === prefix || this.currentPath.startsWith(`${prefix}/`)
    );
  }

  private updateFromUrl(url: string): void {
    const path = url.split('?')[0];
    this.currentPath = path;
    this.minimal =
      path.startsWith('/auth/handoff') ||
      path.startsWith('/share/') ||
      /^\/p\/[^/]+/.test(path);
    if (this.variant === 'portal' && path.startsWith('/portal')) {
      void this.loadPortalCapabilities();
    }
  }

  toggleMobile(): void {
    this.closeAccountMenu();
    this.closeNotificationsMenu();
    this.mobileOpen.update((v) => !v);
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }

  toggleAccountMenu(): void {
    this.closeMobile();
    this.closeNotificationsMenu();
    this.accountMenuOpen.update((v) => !v);
  }

  closeAccountMenu(): void {
    this.accountMenuOpen.set(false);
  }

  async openAccountProfile(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.closeAccountMenu();
    await this.router.navigate(['/account/profile']);
  }

  async toggleNotificationsMenu(): Promise<void> {
    this.closeMobile();
    this.closeAccountMenu();
    const next = !this.notificationsMenuOpen();
    this.notificationsMenuOpen.set(next);
    if (next) {
      await this.loadRecentNotifications();
    }
  }

  closeNotificationsMenu(): void {
    this.notificationsMenuOpen.set(false);
  }

  async refreshUnseenCount(): Promise<void> {
    try {
      this.unseenCount = await firstValueFrom(
        this.api.getLearnerUnseenNotificationCount(),
      );
    } catch {
      // Keep last known count.
    }
  }

  async loadRecentNotifications(): Promise<void> {
    this.notificationsLoading = true;
    try {
      this.recentNotifications = await firstValueFrom(
        this.api.listLearnerNotifications(8, 0),
      );
      await firstValueFrom(this.api.markAllLearnerNotificationsAsSeen());
      this.unseenCount = 0;
      this.actionRequiredCount = this.recentNotifications.filter((item) =>
        isNotificationActionRequired(item),
      ).length;
    } catch {
      this.recentNotifications = [];
    } finally {
      this.notificationsLoading = false;
    }
  }

  async openNotification(item: NotificationResponse): Promise<void> {
    this.closeNotificationsMenu();
    try {
      if (!item.isRead) {
        await firstValueFrom(this.api.markLearnerNotificationAsRead(item.id));
      }
    } catch {
      // Continue navigation.
    }
    const route = notificationDeepLinkRoute(item);
    if (route) {
      await this.router.navigate(route);
      return;
    }
    await this.router.navigate(['/notifications']);
  }

  isActionRequired(item: NotificationResponse): boolean {
    return isNotificationActionRequired(item);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (!target.closest('.site-nav-account')) {
      this.closeAccountMenu();
    }
    if (!target.closest('.site-nav-notifications')) {
      this.closeNotificationsMenu();
    }
  }

  signOut(): void {
    this.closeAccountMenu();
    this.closeNotificationsMenu();
    this.closeMobile();
    const landing =
      this.variant === 'portal' ? '/partner/login' : '/learner/login';
    void this.auth.logout().then(() => this.router.navigate([landing]));
  }

  private async loadPortalCapabilities(): Promise<void> {
    const accountId = this.auth.getAccountId();
    if (!accountId) {
      this.portalCapsLoaded = false;
      this.portalAccountName = '';
      this.portalPartnerEnabled = false;
      this.portalReferralEnabled = false;
      return;
    }
    if (this.portalCapsLoaded) {
      return;
    }
    try {
      const caps = await firstValueFrom(
        this.api.getAccountPortalCapabilities(accountId),
      );
      this.portalAccountName = caps.accountName || '';
      this.portalPartnerEnabled = !!caps.partnerEnabled;
      this.portalReferralEnabled = !!caps.referralEnabled;
      this.portalCapsLoaded = true;
    } catch {
      // Keep defaults; portal pages still load their own data.
    }
  }

  async careerChallengesLink(): Promise<void> {
    if (this.currentPath.startsWith('/career-challenges/')) {
      return;
    }
    let route = '/career-challenges';
    try {
      const journey = await firstValueFrom(this.api.getLearnerOnboardingJourney());
      const target = resolveOnboardingRouteFromJourney(journey);
      if (target.startsWith('/career-challenges/')) {
        route = target;
      }
    } catch {
    }
    void this.router.navigateByUrl(route);
  }

  private async refreshEmailVerificationBanner(): Promise<void> {
    try {
      const profile = await firstValueFrom(this.api.getLearnerProfile());
      this.emailVerificationNeeded =
        !!profile.email?.trim() && !profile.emailConfirmed;
    } catch {
      this.emailVerificationNeeded = false;
    }
  }
}
